// "Guide me" (2026-10-08): the last stretch on foot to an asset or a
// project's pipes - one big arrow (bearing from GPS to the target, turned by
// the compass heading) and a distance countdown, in a card that stays on top
// of every field view (map, radar, AR). The radar draws the target too (a
// marker, or an edge arrow when beyond its range).
//
// Honest about GPS: the card always says how good the fix is; "arrived"
// means within ~10 m or within the GPS accuracy if that is worse; inside the
// GPS error it says so instead of pretending to point. Works offline: the
// target geometry is held here once loaded (and the worker caches the
// locate / project-site answers), so nothing needs the network while walking.
//
// A project: by default the nearest point of ANY of its pipes; "Next asset"
// steps through its pipes one by one (nearest-neighbour order from where
// the user started), each with its own arrow and arrival.
import { h, fill } from "./dom";
import type { GuideState, GuideTargetPath, LonLat } from "./geoMath";
import { arrivalRadiusM, distanceM, fmtDistance, guideState, nearestTarget, preferredDirectionsLink, stepOrder, turnDeg } from "./geoMath";

export interface GuideItem extends GuideTargetPath {
  label?: string;
}

export interface GuideSpec {
  kind: "asset" | "project";
  /** the asset id, or the project id */
  id: string;
  label: string;
  items: GuideItem[];
}

export interface GuideHost {
  getFix: () => { lat: number; lon: number; accuracy: number } | null;
  getHeading: () => number | null;
  /** within the arrival radius of `assetId` (once per target) */
  onArrive: (assetId: string) => void;
  /** the target point / step changed (redraw the radar, map) */
  onChange: () => void;
  onStop: () => void;
}

interface Live {
  spec: GuideSpec;
  host: GuideHost;
  el: HTMLElement;
  /** step-through order (asset ids) for a project; [] for an asset */
  order: string[];
  /** -1 = the nearest of all the project's pipes */
  step: number;
  arrivedFor: string | null;
  arrowShown: number;
  last: { id: string; lat: number; lon: number; state: GuideState } | null;
}

let G: Live | null = null;

export function guiding(): boolean {
  return G !== null;
}

/** The current target for the radar / map: point, label, arrived. */
export function guideTarget(): { id: string; lat: number; lon: number; label: string; arrived: boolean } | null {
  if (!G?.last) return null;
  const it = G.spec.items.find((i) => i.id === G?.last?.id);
  return { id: G.last.id, lat: G.last.lat, lon: G.last.lon, label: it?.label ?? G.spec.label, arrived: G.last.state.arrived };
}

function currentItems(g: Live): GuideItem[] {
  if (g.step < 0 || !g.order.length) return g.spec.items;
  const id = g.order[g.step];
  return g.spec.items.filter((i) => i.id === id);
}

export function startGuide(spec: GuideSpec, host: GuideHost, mount: HTMLElement): void {
  stopGuide(false);
  const el = h("div", { class: "fa-guide", role: "status", "aria-live": "polite" });
  mount.append(el);
  const fix = host.getFix();
  G = {
    spec, host, el,
    order: spec.kind === "project" && spec.items.length > 1 && fix ? stepOrder(fix.lat, fix.lon, spec.items)
      : spec.kind === "project" ? spec.items.map((i) => i.id) : [],
    step: -1, arrivedFor: null, arrowShown: 0, last: null,
  };
  updateGuide(true);
}

export function stopGuide(notify = true): void {
  if (!G) return;
  const host = G.host;
  G.el.remove();
  G = null;
  if (notify) {
    host.onStop();
    host.onChange();
  }
}

function setStep(step: number): void {
  if (!G) return;
  G.step = step;
  G.arrivedFor = null;
  updateGuide(true);
}

/** Re-render the card from the latest fix + heading (cheap: call it on every
 * GPS update and heading tick). */
export function updateGuide(rebuild = false): void {
  const g = G;
  if (!g) return;
  const fix = g.host.getFix();
  const heading = g.host.getHeading();
  let changed = rebuild;
  if (fix) {
    const near = nearestTarget(fix.lat, fix.lon, currentItems(g));
    if (near) {
      const state = guideState(fix, near, heading);
      if (!g.last || g.last.id !== near.id || distanceM(g.last.lat, g.last.lon, near.lat, near.lon) > 0.5 ||
          g.last.state.arrived !== state.arrived) changed = true;
      g.last = { id: near.id, lat: near.lat, lon: near.lon, state };
      if (state.arrived && g.arrivedFor !== near.id) {
        g.arrivedFor = near.id;
        g.host.onArrive(near.id);
      }
    }
  }
  render(g, fix, rebuild);
  if (changed) g.host.onChange();
}

function directionsHref(g: Live, fix: { lat: number; lon: number } | null): string | null {
  const t = g.last;
  if (!t) {
    // no fix yet: the first item's location
    const it = g.spec.items[0];
    const p: LonLat | null = it?.path?.length ? it.path[Math.floor(it.path.length / 2)]
      : typeof it?.lon === "number" && typeof it?.lat === "number" ? [it.lon, it.lat] : null;
    return p ? preferredDirectionsLink(navigator.userAgent, p[1], p[0], "driving", g.spec.label) : null;
  }
  const far = fix ? distanceM(fix.lat, fix.lon, t.lat, t.lon) > 1500 : true;
  return preferredDirectionsLink(navigator.userAgent, t.lat, t.lon, far ? "driving" : "walking", g.spec.label);
}

let parts: { arrow: HTMLElement; dist: HTMLElement; what: HTMLElement; note: HTMLElement; link: HTMLAnchorElement; steps: HTMLElement } | null = null;

function render(g: Live, fix: { lat: number; lon: number; accuracy: number } | null, rebuild: boolean): void {
  if (rebuild || !parts || !g.el.contains(parts.arrow)) {
    const arrow = h("div", { class: "fa-guide-arrow", "aria-hidden": "true" }, "↑");
    const dist = h("div", { class: "fa-guide-dist" });
    const what = h("div", { class: "fa-guide-what" });
    const note = h("div", { class: "fa-guide-note" });
    const link = h("a", { class: "fa-btn fa-guide-maps", target: "_blank", rel: "noopener" }, "Directions in Maps") as HTMLAnchorElement;
    const steps = h("div", { class: "fa-guide-steps" });
    const stop = h("button", { class: "fa-link fa-guide-stop", "aria-label": "Stop guiding", onclick: () => stopGuide() }, "✕");
    fill(g.el, h("div", { class: "fa-guide-main" }, arrow, h("div", { class: "fa-guide-text" }, dist, what), stop), note,
      h("div", { class: "fa-guide-actions" }, steps, link));
    parts = { arrow, dist, what, note, link, steps };
    g.arrowShown = 0;
  }
  const p = parts;
  const t = g.last;
  const it = t ? g.spec.items.find((i) => i.id === t.id) : undefined;
  const stepTxt = g.order.length > 1 && g.step >= 0 ? ` (${g.step + 1} of ${g.order.length})` : "";
  p.what.textContent = g.spec.kind === "project"
    ? `${g.spec.label}${g.step < 0 ? " - nearest pipe" : ""}${stepTxt}${it?.label ? ` · ${it.label}` : t ? ` · ${t.id}` : ""}`
    : `${g.spec.label}${t && t.id !== g.spec.label ? ` · ${t.id}` : ""}`;
  const href = directionsHref(g, fix);
  if (href) p.link.href = href;
  p.link.hidden = !href;
  if (!fix) {
    p.dist.textContent = "Waiting for GPS…";
    p.note.textContent = "Start location (the field app's start button) to be guided.";
    g.el.classList.remove("arrived");
    p.arrow.style.opacity = "0.3";
  } else if (!t) {
    p.dist.textContent = "—";
    p.note.textContent = "This target has no location in our data.";
  } else {
    const s = t.state;
    g.el.classList.toggle("arrived", s.arrived);
    p.arrow.style.opacity = s.withinGpsError && !s.arrived ? "0.45" : "1";
    // rotate the shortest way (no 350-degree spins when crossing north)
    g.arrowShown += turnDeg(s.arrowDeg, g.arrowShown);
    p.arrow.style.transform = `rotate(${g.arrowShown}deg)`;
    p.arrow.textContent = s.arrived ? "✓" : "↑";
    if (s.arrived) p.arrow.style.transform = "none";
    p.dist.textContent = s.arrived ? `You're there` : fmtDistance(s.distance);
    const acc = Math.round(fix.accuracy);
    const bits: string[] = [];
    if (s.arrived) bits.push(`Within ${Math.round(arrivalRadiusM(fix.accuracy))} m. It's highlighted - look for it here (GPS ±${acc} m).`);
    else if (s.withinGpsError) bits.push(`Closer than the GPS can tell (±${acc} m): look around for it.`);
    else bits.push(`GPS ±${acc} m${acc > 25 ? " - weak fix, the arrow may wander" : ""}.`);
    if (!s.headingKnown && !s.arrived) bits.push("No compass: the arrow points relative to NORTH (hold the phone with north up).");
    if (!navigator.onLine) bits.push("Offline: guiding from the saved location.");
    p.note.textContent = bits.join(" ");
  }
  // step-through (projects)
  if (rebuild) {
    const n = g.order.length;
    fill(p.steps,
      n > 1 && g.step >= 0 ? h("button", { class: "fa-chip", onclick: () => setStep(g.step <= 0 ? -1 : g.step - 1) }, "‹ Prev") : null,
      n > 1 ? h("button", { class: "fa-chip", onclick: () => setStep(g.step + 1 >= n ? -1 : g.step + 1) },
        g.step < 0 ? "Step through pipes ›" : g.step + 1 >= n ? "Nearest again" : "Next asset ›") : null);
  }
}

/** The field app rebuilt its root (render()): put the card back. */
export function remountGuide(mount: HTMLElement): void {
  if (G && !mount.contains(G.el)) mount.append(G.el);
}
